// anim.jsx — robust motion utilities for Mayanos.
// Key principle: content is ALWAYS visible at rest. We only ever hide-then-reveal
// when the document is actually visible (a real, focused browser) and the user
// hasn't asked for reduced motion. In any other context (backgrounded render,
// reduced-motion, no WAAPI) we no-op and leave content fully visible — so nothing
// can ever get stuck hidden.

const EASE_OUT = "cubic-bezier(.22,.61,.36,1)";
const EASE_SPRING = "cubic-bezier(.34,1.56,.64,1)";
const EASE_SOFT = "cubic-bezier(.4,0,.2,1)";

const _reduced = () =>
  typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const canAnimate = () =>
  !_reduced() &&
  typeof document !== "undefined" &&
  document.visibilityState === "visible" &&
  typeof Element !== "undefined" && !!Element.prototype.animate;

// Core entrance: fade + translate (+ optional scale/blur).
// Robust: sets the start state inline (no flicker during delay), clears it on
// finish, AND cancels+clears if the document goes hidden mid-flight — so an
// element can never get stuck offset or invisible. No-ops when we can't animate.
function entrance(el, opts = {}) {
  if (!el || !canAnimate()) return null;
  const { y = 16, x = 0, s = 1, dur = 640, delay = 0, blur = 0, easing = EASE_OUT } = opts;
  const useBlur = blur > 0;
  // start state inline so the delay window shows hidden (not a flash of final)
  el.style.opacity = "0";
  el.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
  if (useBlur) el.style.filter = `blur(${blur}px)`;
  el.style.willChange = "opacity, transform";
  const a = el.animate(
    [
      { opacity: 0, transform: `translate(${x}px, ${y}px) scale(${s})`, ...(useBlur ? { filter: `blur(${blur}px)` } : {}) },
      { opacity: 1, transform: "translate(0,0) scale(1)", ...(useBlur ? { filter: "blur(0px)" } : {}) },
    ],
    { duration: dur, delay, easing, fill: "none" }
  );
  const clear = () => {
    el.style.opacity = ""; el.style.transform = ""; el.style.filter = ""; el.style.willChange = "";
    document.removeEventListener("visibilitychange", onHide);
    clearTimeout(safety);
  };
  function onHide() { if (document.visibilityState === "hidden") { try { a.cancel(); } catch (e) {} clear(); } }
  a.onfinish = clear;
  a.oncancel = clear;
  document.addEventListener("visibilitychange", onHide);
  // wall-clock safety: if the animation stalls (throttled/hidden iframe with no
  // visibilitychange event), force the element back to its visible base state.
  const safety = setTimeout(() => { try { a.cancel(); } catch (e) {} clear(); }, delay + dur + 900);
  return a;
}

// Hook: animate a single element on mount (and when `deps` change).
function useEntrance(opts = {}, deps = []) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => { entrance(ref.current, opts); }, deps);
  return ref;
}

// Hook: stagger direct children (or a selector) on mount / dep change.
function useStagger(opts = {}, deps = []) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const root = ref.current;
    if (!root || !canAnimate()) return;
    const sel = opts.selector;
    const items = sel ? root.querySelectorAll(sel) : root.children;
    const base = opts.delay || 0;
    const step = opts.step != null ? opts.step : 80;
    Array.prototype.forEach.call(items, (el, i) =>
      entrance(el, { y: opts.y, x: opts.x, s: opts.s, blur: opts.blur, dur: opts.dur, easing: opts.easing, delay: base + i * step })
    );
  }, deps);
  return ref;
}

// Hook: reveal [data-reveal] descendants when they scroll into view (one-shot).
// Gated by canAnimate; if we can't animate we never hide, so content stays visible.
function useScrollReveal(deps = []) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const root = ref.current;
    if (!root || !canAnimate() || !("IntersectionObserver" in window)) return;
    const els = Array.prototype.slice.call(root.querySelectorAll("[data-reveal]"));
    if (!els.length) return;
    // hide synchronously (before paint) to avoid flash
    els.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(22px)";
      el.style.willChange = "opacity, transform";
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const el = e.target;
        const delay = parseInt(el.dataset.revealDelay || "0", 10);
        const a = entrance(el, { y: 22, dur: 720, delay });
        const clear = () => { el.style.opacity = ""; el.style.transform = ""; el.style.willChange = ""; };
        if (a) a.onfinish = clear; else clear();
        io.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    els.forEach((el) => io.observe(el));
    // safety net: if anything is still hidden after 2.5s (e.g. tab blurred mid-load), reveal it
    const safety = setTimeout(() => els.forEach((el) => {
      if (el.style.opacity === "0") { el.style.opacity = ""; el.style.transform = ""; el.style.willChange = ""; }
    }), 2500);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, deps);
  return ref;
}

// Count-up a number on mount (for stat figures). Leaves final value if it can't run.
function useCountUp(target, opts = {}) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const finalStr = String(target);
    const num = parseFloat(String(target).replace(/[^0-9.]/g, ""));
    const suffix = String(target).replace(/[0-9.,]/g, "");
    if (!canAnimate() || !isFinite(num)) { el.textContent = finalStr; return; }
    const dur = opts.dur || 1100;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = Math.round(num * eased);
      el.textContent = (opts.comma ? val.toLocaleString() : val) + suffix;
      if (p < 1) requestAnimationFrame(tick); else el.textContent = finalStr;
    };
    requestAnimationFrame(tick);
  }, []);
  return ref;
}

Object.assign(window, {
  EASE_OUT, EASE_SPRING, EASE_SOFT, canAnimate, entrance,
  useEntrance, useStagger, useScrollReveal, useCountUp,
  useMediaQuery, useIsMobile,
});

// Reactive media-query hook.
function useMediaQuery(query) {
  const get = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia(query).matches;
  const [match, setMatch] = React.useState(get);
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else mq.removeListener(on); };
  }, [query]);
  return match;
}
function useIsMobile() { return useMediaQuery("(max-width: 820px), (max-height: 560px) and (orientation: landscape)"); }
